import React from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import { formatCAD } from "../../utils/format";

const Item = ({ item, onAdd, disableItem, quantityInCart = 0 }) => {
	const longPressTimeout = React.useRef(null);
	const longPressTriggered = React.useRef(false);
	const pointerIdRef = React.useRef(null);
	const LONG_PRESS_MS = 500;
	// `disabled` here is register-sellability, toggled live by the
	// long-press gesture below -- it's intentionally NOT initialized from
	// `item.enabled_menu` (that field controls the separate customer-facing
	// MEnu display, which switched to `enabled_pos` for its own visibility
	// gate; conflating the two here meant most of the real catalog -- which
	// defaults enabled_menu to false until someone curates the customer
	// menu -- couldn't be added to cart at the register at all).
	// `enabledPOS === false` below is the actual "not sellable" gate.
	const [disabled, setDisabled] = React.useState(false);

	if (item && item.enabledPOS === false) return null;

	const handlePointerDown = (e) => {
		longPressTriggered.current = false;
		try {
			e.currentTarget.setPointerCapture?.(e.pointerId);
			pointerIdRef.current = e.pointerId;
		} catch (_) {}

		longPressTimeout.current = setTimeout(() => {
			longPressTriggered.current = true;
			setDisabled((prev) => {
				const newState = !prev;
				if (disableItem) disableItem(item.$id, !newState);
				return newState;
			});
		}, LONG_PRESS_MS);
	};

	const clearLongPress = (e) => {
		if (longPressTimeout.current) {
			clearTimeout(longPressTimeout.current);
			longPressTimeout.current = null;
		}
		try {
			if (pointerIdRef.current != null) {
				e.currentTarget.releasePointerCapture?.(pointerIdRef.current);
				pointerIdRef.current = null;
			}
		} catch (_) {}
	};

	const bgUrl = item && item.image ? item.image : "/logo192.png";

	const isDisabled = Boolean(item.disabled) || disabled;

	return (
		<Button
			aria-disabled={isDisabled}
			key={item.$id}
			onPointerDown={handlePointerDown}
			onPointerUp={clearLongPress}
			onPointerCancel={clearLongPress}
			onPointerLeave={clearLongPress}
			onClick={(e) => {
				if (longPressTriggered.current) {
					longPressTriggered.current = false;
					e.preventDefault();
					e.stopPropagation();
					return;
				}
				if (isDisabled) {
					e.preventDefault();
					e.stopPropagation();
					return;
				}
				onAdd(item.$id);
			}}
			variant="contained"
			color="primary"
			sx={{
				width: "100%",
				aspectRatio: "1",
				borderRadius: "8px",
				p: 0.85,
				textTransform: "none",
				display: "flex",
				flexDirection: "column",
				justifyContent: "space-between",
				alignItems: "stretch",
				overflow: "hidden",
				gap: 0.15,
				boxShadow: (theme) =>
					theme?.shadows?.[1] ??
					"0 2px 6px rgba(255, 255, 255, 0.12)",
				transition: "transform 120ms ease, box-shadow 120ms ease",
				position: "relative",
				"&::before": {
					content: '""',
					position: "absolute",
					inset: 0,
					backgroundImage: bgUrl ? `url(${bgUrl})` : "none",
					backgroundSize: "cover",
					backgroundPosition: "center",
					backgroundRepeat: "no-repeat",
					filter: "blur(1px) saturate(0.98)",
					transform: "scale(1)",
					zIndex: 0,
					transition: "transform 180ms ease, filter 180ms ease",
				},
				"&:hover::before": {
					transform: "scale(1.2)",
				},
				"&::after": {
					content: '""',
					position: "absolute",
					inset: 0,
					background: "rgba(0,0,0,0.45)",
					zIndex: 1,
				},
				"& > .itemContent": {
					position: "relative",
					zIndex: 2,
				},
				...(isDisabled
					? {
							opacity: 0.5,
							filter: "grayscale(0.6)",
					  }
					: {}),
			}}
		>
			{quantityInCart > 0 && (
				<Box
					sx={{
						position: "absolute",
						top: 4,
						right: 4,
						zIndex: 3,
						minWidth: 18,
						height: 18,
						borderRadius: "9px",
						px: 0.5,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						fontSize: "0.7rem",
						fontWeight: 700,
						color: "primary.contrastText",
						backgroundColor: "secondary.main",
						boxShadow: 2,
					}}
				>
					{quantityInCart}
				</Box>
			)}
			<Box
				className="itemContent"
				sx={{
					display: "flex",
					alignItems: "flex-start",
					minHeight: 0,
				}}
			>
				<Typography
					variant="h6"
					sx={{
						fontSize: "1.05rem",
						fontWeight: 600,
						lineHeight: 1.05,
						wordBreak: "break-word",
						overflowWrap: "anywhere",
						mb: 0.15,
						textShadow: "0 1px 3px rgba(0,0,0,0.9)",
					}}
				>
					{item.name}
				</Typography>
			</Box>

			<Box
				className="itemContent"
				sx={{
					flex: 1,
					display: "flex",
					alignItems: "stretch",
					flexDirection: "column",
					justifyContent: "flex-end",
				}}
			>
				<Typography
					variant="body2"
					sx={{
						color: "text.secondary",
						fontSize: "0.65rem",
						lineHeight: 1.12,
						wordBreak: "break-word",
						overflowWrap: "anywhere",
						mb: 0.5,
						textShadow: "0 1px 2px rgba(0,0,0,0.9)",
					}}
				>
					{item.description}
				</Typography>

				<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
					<Chip
						label={formatCAD(item.price)}
						color="default"
						size="small"
						sx={{
							fontWeight: 600,
							height: "18px",
							fontSize: "0.85rem",
						}}
					/>
				</Box>
			</Box>
		</Button>
	);
};
export default Item;
